import React from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer, QrCode } from "lucide-react";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * TicketQrPanel — SS-7 (Jun 2026).
 *
 * Canonical QR section for the admin ticket detail. Reads / mints via
 * the SS-7 endpoints (`GET/POST /admin/racket-lab/tickets/{id}/qr-canonical`),
 * which sit on the existing qr_tokens infra (kind=ticket, scope=canonical,
 * idempotent — no duplicates). NO public scan: the payload is an internal
 * `teamos://` URI.
 */
export const TicketQrPanel = ({ ticketId }) => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const { data, loading, reload } = useAdminApi(
    `/admin/racket-lab/tickets/${encodeURIComponent(ticketId)}/qr-canonical`,
    { skip: !ticketId },
  );
  const { run, loading: minting } = useAdminMutation();

  const generate = async () => {
    const r = await run(
      "post",
      `/admin/racket-lab/tickets/${encodeURIComponent(ticketId)}/qr-canonical`,
      {},
    ).catch(() => null);
    if (r) reload();
  };

  return (
    <section data-testid="rl-ticket-qr-panel" className="rounded-md border border-slate-200 bg-white p-4">
      <h2 className="mb-1 flex items-center gap-1.5 text-sm font-medium text-slate-700">
        <QrCode className="h-3.5 w-3.5 text-slate-400" />
        {t("tickets.qr_canonical.title")}
      </h2>
      <p className="mb-3 text-[10px] text-slate-400">{t("tickets.qr_canonical.subtitle")}</p>

      {loading ? (
        <p className="text-xs text-slate-500">{t("tickets.qr_canonical.loading")}</p>
      ) : data?.exists && data?.qr_payload ? (
        <div className="flex flex-wrap items-start gap-4">
          <div data-testid="rl-ticket-qr-svg" className="rounded-md border border-slate-200 bg-white p-2">
            <QRCodeSVG value={data.qr_payload} size={128} />
          </div>
          <div className="flex flex-col gap-1 text-xs">
            <p>
              <span className="text-slate-500">{t("tickets.qr_canonical.payload_label")}:</span>{" "}
              <code data-testid="rl-ticket-qr-payload" className="break-all text-[10px] text-slate-700">
                {data.qr_payload}
              </code>
            </p>
            <p>
              <span className="text-slate-500">{t("tickets.qr_canonical.expires_label")}:</span>{" "}
              <span data-testid="rl-ticket-qr-expires">{data.qr_token?.expires_at?.slice(0, 10) || "—"}</span>
            </p>
            <p>
              <span className="text-slate-500">{t("tickets.qr_canonical.scan_count_label")}:</span>{" "}
              <span data-testid="rl-ticket-qr-scan-count">{data.qr_token?.scan_count ?? 0}</span>
            </p>
            <button
              type="button"
              data-testid="rl-ticket-qr-print-link"
              onClick={() => navigate(`/admin/racket-lab/tickets/${ticketId}/qr-print`)}
              className="mt-1 inline-flex w-fit items-center gap-1 rounded-md bg-slate-800 px-2.5 py-1 text-[10px] font-medium text-white hover:bg-slate-900"
            >
              <Printer className="h-3 w-3" />
              {t("tickets.qr_canonical.print_view")}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-start gap-2">
          <p data-testid="rl-ticket-qr-none" className="text-xs text-slate-500">
            {t("tickets.qr_canonical.none")}
          </p>
          <button
            type="button"
            data-testid="rl-ticket-qr-generate-btn"
            onClick={generate}
            disabled={minting}
            className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <QrCode className="h-3.5 w-3.5" />
            {minting ? t("tickets.qr_canonical.generating") : t("tickets.qr_canonical.generate")}
          </button>
        </div>
      )}
    </section>
  );
};

export default TicketQrPanel;
